import React, { useState } from 'react';

import { FormGroup } from 'semantic';
import { useTranslation } from 'react-i18next';
import { Button } from 'semantic-ui-react';
import { size } from 'lodash-es';

import CardStyleRadioButton from 'components/form-fields/CardStyleRadioButton';
import {
  ChargingGroup,
  ChargingGroupSearchAPIResponse,
} from 'subdomains/cpo/types/charging-policy';
import { ChargingGroupPreview } from 'subdomains/cpo/screens/policies-and-groups/screens/provider-admin/charging-groups/ChargingGroupPreview';
import { request } from 'utils/api';
import { ChargingGroupModal } from 'subdomains/cpo/screens/policies-and-groups/screens/provider-admin/charging-groups/form/ChargingGroupModal';
import { CHARGING_GROUPS_V1_SEARCH_API_URL } from 'subdomains/cpo/router/api';
import { AsyncDropdownField } from 'subdomains/cpo/components/AsyncDropdownField';
import { useQuery } from '@tanstack/react-query';

type Props = {
  values?: string[];
  onChange?: (values: string[]) => void;
};

const ChargingPolicyAssociationSelection: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [isAllSelected, setIsAllSelected] = useState(!props.values?.length);
  const [selectedChargingGroupsIds, setSelectedChargingGroupsIds] = useState<
    string[]
  >(props.values ?? []);

  const { data: chargingGroupsResponse, refetch: refetchChargingGroups } =
    useQuery<ChargingGroupSearchAPIResponse>({
      queryKey: ['charging-groups', selectedChargingGroupsIds],
      queryFn: async () => {
        const { data, meta } = await request<ChargingGroupSearchAPIResponse>({
          path: CHARGING_GROUPS_V1_SEARCH_API_URL,
          method: 'POST',
          body: {
            ids: selectedChargingGroupsIds,
          },
        });

        return { data, meta };
      },
      enabled: size(selectedChargingGroupsIds) > 0,
    });

  const chargingGroupQueryFn = async (
    body: object
  ): Promise<ChargingGroup[]> => {
    const { data } = await request<ChargingGroupSearchAPIResponse>({
      path: CHARGING_GROUPS_V1_SEARCH_API_URL,
      method: 'POST',
      body,
    });

    return data.chargingGroups;
  };

  const handleModeChange = (isAll: boolean) => {
    if (isAll !== isAllSelected) {
      setSelectedChargingGroupsIds([]);
    }
    setIsAllSelected(isAll);
  };

  const handleOnChange = (selectedChargingGroupsIds: string[]) => {
    setSelectedChargingGroupsIds(selectedChargingGroupsIds);
    props.onChange?.(selectedChargingGroupsIds);
  };

  const handleOnCreated = (id: string) => {
    const newSelectedChargingGroupsIds = [...selectedChargingGroupsIds, id];
    setSelectedChargingGroupsIds(newSelectedChargingGroupsIds);
    props.onChange?.(newSelectedChargingGroupsIds);
  };

  const chargingGroupOptionsQueryFn = async (body: object) => {
    const data = await chargingGroupQueryFn(body);

    return data.map((group) => ({
      value: group.id,
      text: group.label,
    }));
  };

  return (
    <>
      <FormGroup style={{ marginLeft: '0', marginRight: '0' }}>
        <CardStyleRadioButton
          isSelected={isAllSelected}
          onClick={() => handleModeChange(true)}
          text={t(
            'providerAdmin.chargingPolicyForm.associationSelection.allChargingStationsButtonText',
            'All Charging Stations'
          )}
        />
        <CardStyleRadioButton
          isSelected={!isAllSelected}
          onClick={() => handleModeChange(false)}
          text={t(
            'providerAdmin.chargingPolicyForm.associationSelection.chargingGroupsButtonText',
            'Charging Groups'
          )}
        />
      </FormGroup>
      {!isAllSelected && (
        <>
          <AsyncDropdownField
            identifier="charging-group-selection"
            label={t(
              'providerAdmin.chargingPolicyForm.associationSelection.selectChargingGroupsLabel',
              'Select Charging Groups'
            )}
            value={selectedChargingGroupsIds}
            multiple
            searchable
            onChange={(ids) => handleOnChange(ids as string[])}
            initialOptionsQueryFn={async (value) => {
              const values = value as string[];
              return chargingGroupOptionsQueryFn({
                ...(size(values) > 0 ? { ids: values } : {}),
              });
            }}
            searchOptionsQueryFn={async (searchTerm?: string) => {
              return chargingGroupOptionsQueryFn({
                ...(searchTerm && { search: searchTerm }),
              });
            }}
          />
          <ChargingGroupModal
            trigger={
              <Button
                basic
                icon="plus"
                style={{ marginLeft: '0' }}
                content={t(
                  'providerAdmin.chargingPolicyForm.associationSelection.addChargingGroupButtonText',
                  'New Charging Group'
                )}
                onClick={(e) => e.preventDefault()}
              />
            }
            mode="create"
            onSuccess={(id) => handleOnCreated(id!)}
          />
        </>
      )}

      {selectedChargingGroupsIds.map((id) => {
        const group = chargingGroupsResponse?.data?.chargingGroups.find(
          (g) => g.id === id
        );
        if (!group) {
          return null;
        }

        const numConnectors = group.filters?.reduce((numConnectors, filter) => {
          if (filter.connectors?.length) {
            numConnectors += filter.connectors.length;
          }
          return numConnectors;
        }, 0);

        return (
          <ChargingGroupPreview
            label={group.label}
            numConnectors={numConnectors}
            key={group.id}
            actionComponents={
              <>
                <ChargingGroupModal
                  trigger={
                    <Button
                      basic
                      icon="pen-to-square"
                      title={t('common.edit', 'Edit')}
                      onClick={(e) => e.preventDefault()}
                    />
                  }
                  onSuccess={() => refetchChargingGroups()}
                  chargingGroup={group}
                  mode="edit"
                />
              </>
            }
          />
        );
      })}
    </>
  );
};

export default ChargingPolicyAssociationSelection;
